import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useTenant } from '@/services/tenant-context';
import { subscriptionsApi } from '@/services/subscriptions-api';
import type { KoshRecurringStatus } from '@/types/subscriptions';

function useScope(): string {
  return useTenant().currentTenant.id;
}

export function useSubscriptions(params?: Record<string, string | number | undefined>) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'subscriptions', scope, params ?? {}],
    queryFn: () => subscriptionsApi.list({ pageSize: 200, ...params }),
  });
}

export function useSubscription(id: string | undefined) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'subscriptions', scope, id],
    queryFn: () => subscriptionsApi.get(id!),
    enabled: Boolean(id),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return (keys: QueryKey[]) => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
}

export function useSubscriptionTransition() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshRecurringStatus }) =>
      subscriptionsApi.transition(id, to),
    onSuccess: () => invalidate([['kosh', 'subscriptions']]),
  });
}

export function useRunSubscription() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => subscriptionsApi.run(id),
    onSuccess: () =>
      invalidate([
        ['kosh', 'subscriptions'],
        ['kosh', 'invoices'],
        ['kosh', 'outstandings'],
      ]),
  });
}
